import { faqRowsFromBuiltIn, type FaqRow } from '@antreja/ui/faq';
import type {
  AccessCodeInput,
  AccessRuleInput,
  BuildingInput,
  ClubAdminInviteInput,
  ClubAuditFilter,
  ClubAuditPage,
  ClubAdminInviteResult,
  CreatedCode,
  DoorInput,
  EventFilter,
  GroupInput,
  ImportInput,
  ImportResult,
  InstallerRow,
  InvitePersonInput,
  InvitePersonResult,
  LocationInput,
  OrganizationInput,
  PersonInput,
  Repository,
  Session,
  StaffInviteInput,
  StaffInviteResult,
  ClubInstallerRow,
} from '../repository';
import type { LockConfigState,
  AccessCode,
  AccessRule,
  AuditContext,
  AuditEntry,
  Building,
  ClubAdminRow,
  ClubAuditRow,
  Door,
  Group,
  GroupMember,
  Lock,
  LockGrant,
  LockModel,
  LockPlacement,
  OrgRole,
  Organization,
  Person,
  PersonAppStatusRow,
  PlatformRole,
  PortalRole,
  StaffRow,
  SyncJob,
  CardLoan,
  LoanCardRow,
} from '../../lib/types';
import { createDemoDb, type DemoDb } from './seed';
import { MODEL_LABEL } from '../../lib/format';
import { searchInventoryRows, type InventoryQuery, type InventoryResult } from '../../lib/inventory';
import { cleanAddress, formatAddress, validateAddress, type AddressInput } from '../../lib/address';
import { auditCategory } from '../../lib/auditText';

const clone = <T>(v: T): T => structuredClone(v);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const modelName = (m: LockModel) => MODEL_LABEL[m];
const uid = (prefix: string) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;

function rawAddress(a: {
  street: string | null;
  house_number: string | null;
  house_number_addition: string | null;
  postal_code: string | null;
  city: string | null;
  country: string;
}): AddressInput {
  return {
    street: a.street ?? '',
    house_number: a.house_number ?? '',
    house_number_addition: a.house_number_addition ?? '',
    postal_code: a.postal_code ?? '',
    city: a.city ?? '',
    country: a.country || 'NL',
  };
}

export interface DemoOptions {
  latencyMs?: number;
  now?: () => Date;
}

/**
 * In-memory implementation used when no Supabase env is configured. Simulates the
 * access compiler (rules/memberships → lock_grants + sync_jobs) and BLE sync so the
 * "Opgeslagen" vs "Op het slot" distinction can be demonstrated.
 */
export class DemoRepository implements Repository {
  readonly mode = 'demo' as const;
  private db: DemoDb;
  private latency: number;
  private now: () => Date;
  private actor: { profileId: string | null; role: PortalRole | null } = { profileId: null, role: null };
  private auditSeq = 1000;
  private eventSeq = 10000;

  constructor(opts: DemoOptions = {}) {
    this.now = opts.now ?? (() => new Date());
    this.db = createDemoDb(this.now());
    this.latency = opts.latencyMs ?? 150;
  }

  setActor(profileId: string | null, role: PortalRole | null) {
    this.actor = { profileId, role };
  }

  private async wait<T>(v: T): Promise<T> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency));
    return clone(v);
  }

  private nowIso() {
    return this.now().toISOString();
  }

  private audit(
    orgId: string | null,
    action: string,
    entity: string,
    entityId: string | null,
    details: { before?: Record<string, unknown> | null; after?: Record<string, unknown> | null } = {},
  ) {
    this.db.audit_log.unshift({
      id: ++this.auditSeq,
      organization_id: orgId,
      actor_profile_id: this.actor.profileId,
      actor_role: this.actor.role ?? 'unknown',
      action,
      entity,
      entity_id: entityId,
      created_at: this.nowIso(),
      before: details.before ?? null,
      after: details.after ?? null,
    });
  }

  private assertWritable() {
    if (this.actor.role === 'support') throw new Error('Support heeft alleen leesrechten.');
  }

  // ---------- access compiler (simplified) ----------
  private recompile(orgId: string, personIds: string[]) {
    const db = this.db;
    const now = this.nowIso();
    for (const personId of personIds) {
      const person = db.persons.find((p) => p.id === personId);
      const desired = new Map<string, { schedule: LockGrant['schedule']; valid_until: string | null }>();
      if (person && !person.archived_at) {
        for (const m of db.group_members.filter((x) => x.person_id === personId)) {
          const group = db.groups.find((g) => g.id === m.group_id);
          if (!group || group.archived_at) continue;
          for (const r of db.access_rules.filter((x) => x.group_id === m.group_id)) {
            const door = db.doors.find((d) => d.id === r.door_id);
            if (!door?.lock_id) continue;
            const entry = desired.get(door.lock_id) ?? { schedule: [], valid_until: m.valid_until ?? group.valid_until };
            entry.schedule.push({
              weekdays: r.weekdays,
              start_minute: r.all_day ? 0 : r.start_minute,
              end_minute: r.all_day ? 1440 : r.end_minute,
            });
            desired.set(door.lock_id, entry);
          }
        }
      }
      // upserts
      for (const [lockId, want] of desired) {
        const existing = db.lock_grants.find((g) => g.person_id === personId && g.lock_id === lockId);
        const same =
          existing &&
          existing.sync_state !== 'revoke_pending' &&
          existing.sync_state !== 'revoked' &&
          JSON.stringify(existing.schedule) === JSON.stringify(want.schedule) &&
          existing.valid_until === want.valid_until;
        if (same) continue;
        if (existing) {
          Object.assign(existing, { schedule: want.schedule, valid_until: want.valid_until, sync_state: 'pending' });
        } else {
          db.lock_grants.push({
            id: uid('grant'),
            organization_id: orgId,
            person_id: personId,
            lock_id: lockId,
            valid_from: now,
            valid_until: want.valid_until,
            schedule: want.schedule,
            sync_state: 'pending',
            last_synced_at: null,
          });
        }
        this.enqueue(orgId, lockId, 'upsert_member');
      }
      // removals
      for (const g of db.lock_grants.filter((x) => x.person_id === personId)) {
        if (!desired.has(g.lock_id) && g.sync_state !== 'revoked' && g.sync_state !== 'revoke_pending') {
          g.sync_state = 'revoke_pending';
          this.enqueue(orgId, g.lock_id, 'remove_member');
        }
      }
    }
  }

  private enqueue(orgId: string, lockId: string, action: SyncJob['action']) {
    this.db.sync_jobs.push({
      id: uid('job'),
      organization_id: orgId,
      lock_id: lockId,
      action,
      status: 'awaiting_ble',
      attempts: 0,
      last_error: null,
      requires_ble: true,
      created_at: this.nowIso(),
      completed_at: null,
    });
  }

  // ---------- identity ----------
  async getSessionContext(profileId: string): Promise<Session> {
    const profile = this.db.profiles.find((p) => p.id === profileId);
    if (!profile) throw new Error('Onbekend profiel');
    return this.wait({
      profile,
      platformRole: this.db.platform_staff.find((s) => s.profile_id === profileId)?.role ?? null,
      memberships: this.db.memberships.filter((m) => m.profile_id === profileId),
      managedGroupIds: this.db.group_managers.filter((g) => g.profile_id === profileId).map((g) => g.group_id),
      installerOrgIds: this.db.installer_assignments
        .filter((a) => a.profile_id === profileId && new Date(a.valid_until) > this.now())
        .map((a) => a.organization_id),
    });
  }

  // ---------- tenants ----------
  async listOrganizations() {
    return this.wait(this.db.organizations.filter((o) => !o.archived_at));
  }
  async getOrganization(orgId: string) {
    return this.wait(this.db.organizations.find((o) => o.id === orgId) ?? null);
  }
  async updateOrganization(orgId: string, patch: Partial<Organization>) {
    this.assertWritable();
    const org = this.db.organizations.find((o) => o.id === orgId);
    if (!org) throw new Error('Club niet gevonden');
    Object.assign(org, patch);
    this.audit(orgId, 'organization.update', 'organizations', orgId);
    return this.wait(org);
  }
  async createOrganization(input: OrganizationInput) {
    this.assertWritable();
    const org: Organization = {
      id: uid('org'),
      name: input.name,
      kvk_number: input.kvk_number,
      status: 'trial',
      timezone: 'Europe/Amsterdam',
      log_retention_days: 90,
      battery_alert_pct: 25,
      revoke_alert_hours: 24,
      created_at: this.nowIso(),
      archived_at: null,
    };
    const first = input.first_location;
    const raw = rawAddress(first);
    const firstErr = Object.values(validateAddress(raw, { required: true }))[0];
    if (firstErr) throw new Error(firstErr);
    if (first.name.trim().length < 2) throw new Error('Vul de naam van de locatie in.');
    this.db.organizations.push(org);
    const addr = cleanAddress(raw);
    const locId = uid('loc');
    this.db.locations.push({
      id: locId,
      organization_id: org.id,
      name: first.name.trim(),
      ...addr,
      address: formatAddress({ address: null, ...addr }) || null,
      provider: 'tuya',
      gateway_device_id: null,
    });
    this.audit(org.id, 'organization.create', 'organizations', org.id, { after: { name: org.name } });
    this.audit(org.id, 'location.create', 'locations', locId, { after: { name: first.name.trim() } });
    const email = input.first_admin_email.trim().toLowerCase();
    const profileId = this.findOrCreateProfile(email, input.first_admin_name ?? '');
    this.addClubRole(org.id, profileId, 'club_admin', []);
    this.audit(org.id, 'club_admin.add', 'memberships', `${org.id}:${profileId}`);
    return this.wait(org);
  }
  async listMemberships(orgId: string) {
    return this.wait(
      this.db.memberships
        .filter((m) => m.organization_id === orgId)
        .map((m) => ({ ...m, profile: this.db.profiles.find((p) => p.id === m.profile_id)! })),
    );
  }

  // ---------- club- en groepsbeheerders (spiegelt list_club_admins / add_club_admin / set_member_role / remove_club_role) ----------
  private findOrCreateProfile(email: string, fullName: string): string {
    const existing = Object.entries(this.db.emails).find(([, e]) => e.toLowerCase() === email)?.[0];
    if (existing) return existing;
    const id = uid('prof');
    this.db.profiles.push({ id, full_name: fullName.trim() || email.split('@')[0]!, phone: null, locale: 'nl' });
    this.db.emails[id] = email;
    return id;
  }
  private addClubRole(orgId: string, profileId: string, role: 'club_admin' | 'group_manager', groupIds: string[]): OrgRole {
    for (const gid of groupIds) {
      const g = this.db.groups.find((x) => x.id === gid);
      if (!g || g.organization_id !== orgId) throw new Error('Deze groep hoort niet bij de club.');
    }
    const m = this.db.memberships.find((x) => x.organization_id === orgId && x.profile_id === profileId);
    const next: OrgRole = m?.role === 'club_admin' || role === 'club_admin' ? 'club_admin' : 'group_manager';
    if (m) m.role = next;
    else this.db.memberships.push({ organization_id: orgId, profile_id: profileId, role: next, created_at: this.nowIso() });
    for (const gid of groupIds) {
      if (!this.db.group_managers.some((x) => x.group_id === gid && x.profile_id === profileId)) {
        this.db.group_managers.push({ group_id: gid, profile_id: profileId });
      }
    }
    if (!this.db.persons.some((p) => p.organization_id === orgId && p.profile_id === profileId)) {
      const email = this.db.emails[profileId] ?? null;
      const loose = email
        ? this.db.persons.find((p) => p.organization_id === orgId && !p.profile_id && !p.archived_at && p.email?.toLowerCase() === email)
        : undefined;
      if (loose) loose.profile_id = profileId;
      else {
        const prof = this.db.profiles.find((p) => p.id === profileId)!;
        this.db.persons.push({
          id: uid('pers'),
          organization_id: orgId,
          profile_id: profileId,
          full_name: prof.full_name,
          email,
          phone: null,
          external_ref: null,
          is_minor: false,
          biometric_consent_at: null,
          created_at: this.nowIso(),
          archived_at: null,
        });
      }
    }
    return next;
  }
  /** club_admin van deze club of platform_admin (zoals app.can_club_write). */
  private assertClubAdmin(orgId: string) {
    this.assertWritable();
    const r = this.actor.role;
    if (r === 'platform_admin' || r === null) return;
    const isAdmin = this.db.memberships.some(
      (m) => m.organization_id === orgId && m.profile_id === this.actor.profileId && m.role === 'club_admin',
    );
    if (!isAdmin) throw new Error('Alleen een clubbeheerder of Antreja kan dit.');
  }
  private assertKeepsClubAdmin(orgId: string, profileId: string) {
    const others = this.db.memberships.filter((m) => m.organization_id === orgId && m.role === 'club_admin' && m.profile_id !== profileId);
    if (!others.length) throw new Error('De club moet minstens één clubbeheerder houden.');
  }
  private dropGroupManagement(orgId: string, profileId: string) {
    const groups = new Set(this.db.groups.filter((g) => g.organization_id === orgId).map((g) => g.id));
    this.db.group_managers = this.db.group_managers.filter((g) => !(g.profile_id === profileId && groups.has(g.group_id)));
  }
  async listClubAdmins(orgId: string): Promise<ClubAdminRow[]> {
    const rank = (r: OrgRole) => (r === 'club_admin' ? 0 : 1);
    const rows = this.db.memberships
      .filter((m) => m.organization_id === orgId && m.role !== 'member')
      .map((m) => ({
        profile_id: m.profile_id,
        full_name: this.db.profiles.find((p) => p.id === m.profile_id)?.full_name ?? '',
        email: this.db.emails[m.profile_id] ?? null,
        role: m.role,
        created_at: m.created_at,
        last_sign_in_at: this.db.last_sign_in[m.profile_id] ?? null,
        totp_enabled: this.db.totp_profiles.includes(m.profile_id),
        managed_group_ids: this.db.group_managers
          .filter(
            (g) =>
              g.profile_id === m.profile_id &&
              this.db.groups.some((x) => x.id === g.group_id && x.organization_id === orgId && !x.archived_at),
          )
          .map((g) => g.group_id),
      }))
      .sort((a, b) => rank(a.role) - rank(b.role) || a.full_name.localeCompare(b.full_name));
    return this.wait(rows);
  }
  async resendClubAdminInvite(_orgId: string, profileId: string) {
    const email = this.db.emails[profileId] ?? 'beheerder@demo.invalid';
    return { email };
  }
  async inviteClubAdmin(input: ClubAdminInviteInput): Promise<ClubAdminInviteResult> {
    this.assertClubAdmin(input.organization_id);
    const email = input.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) throw new Error('Dit e-mailadres klopt niet.');
    const existing = Object.values(this.db.emails).some((e) => e.toLowerCase() === email);
    const profileId = this.findOrCreateProfile(email, input.full_name);
    const role = this.addClubRole(input.organization_id, profileId, input.role, input.group_ids);
    this.audit(input.organization_id, 'club_admin.add', 'memberships', `${input.organization_id}:${profileId}`, { after: { role } });
    return this.wait({ profile_id: profileId, role, status: existing ? 'existing_user' : 'invited' });
  }
  async setMemberRole(orgId: string, profileId: string, role: OrgRole) {
    this.assertClubAdmin(orgId);
    const m = this.db.memberships.find((x) => x.organization_id === orgId && x.profile_id === profileId);
    if (!m) throw new Error('Deze persoon heeft geen rol in de club.');
    if (m.role === role) return this.wait(undefined);
    if (m.role === 'club_admin') this.assertKeepsClubAdmin(orgId, profileId);
    const before = m.role;
    m.role = role;
    if (role === 'member') this.dropGroupManagement(orgId, profileId);
    this.audit(orgId, 'membership.role', 'memberships', `${orgId}:${profileId}`, { before: { role: before }, after: { role } });
    return this.wait(undefined);
  }
  async removeClubRole(orgId: string, profileId: string) {
    this.assertClubAdmin(orgId);
    const m = this.db.memberships.find((x) => x.organization_id === orgId && x.profile_id === profileId);
    if (!m || m.role === 'member') return this.wait(undefined);
    if (m.role === 'club_admin') this.assertKeepsClubAdmin(orgId, profileId);
    this.dropGroupManagement(orgId, profileId);
    if (this.db.persons.some((p) => p.organization_id === orgId && p.profile_id === profileId && !p.archived_at)) m.role = 'member';
    else this.db.memberships = this.db.memberships.filter((x) => x !== m);
    this.audit(orgId, 'membership.remove_admin', 'memberships', `${orgId}:${profileId}`);
    return this.wait(undefined);
  }

  // ---------- physical ----------
  async listLocations(orgId: string) {
    return this.wait(this.db.locations.filter((l) => l.organization_id === orgId));
  }
  async listDoors(orgId: string) {
    return this.wait(this.db.doors.filter((d) => d.organization_id === orgId && !d.archived_at));
  }
  async listLocks(orgId: string | null) {
    return this.wait(orgId ? this.db.locks.filter((l) => l.organization_id === orgId) : this.db.locks);
  }
  async searchInventory(q: InventoryQuery): Promise<InventoryResult> {
    const orgName = new Map(this.db.organizations.map((o) => [o.id, o.name]));
    const all = this.db.locks.map((lock) => {
      const door = this.db.doors.find((d) => d.lock_id === lock.id && !d.archived_at) ?? null;
      return {
        lock,
        organization_name: lock.organization_id ? orgName.get(lock.organization_id) ?? null : null,
        door_id: door?.id ?? null,
        door_name: door?.name ?? null,
        location_name: door ? this.db.locations.find((l) => l.id === door.location_id)?.name ?? null : null,
      };
    });
    const res = searchInventoryRows(all, q);
    return this.wait({ rows: clone(res.rows), total: res.total });
  }
  async addLocks(input: { model: LockModel; serials: string[]; firmware: string | null }) {
    this.assertWritable();
    const dup = input.serials.find((s) => this.db.locks.some((l) => l.serial === s));
    if (dup) throw new Error(`Serienummer ${dup} bestaat al.`);
    const created: Lock[] = input.serials.map((serial) => ({
      id: uid('lock'),
      provider: 'tuya',
      model: input.model,
      serial,
      provider_device_id: null,
      firmware: input.firmware,
      api_generation: null,
      status: 'in_stock',
      organization_id: null,
      battery_pct: null,
      battery_reported_at: null,
      battery_alerted_at: null,
      last_seen_at: null,
      last_sync_at: null,
      reset_card_held_by: input.model === 'D1' ? 'Antreja kantoor' : null,
      created_at: this.nowIso(),
    }));
    this.db.locks.push(...created);
    this.audit(null, 'locks.bulk_add', 'locks', String(created.length));
    return this.wait(created);
  }
  async assignLock(lockId: string, orgId: string | null) {
    this.assertWritable();
    const lock = this.db.locks.find((l) => l.id === lockId);
    if (!lock) throw new Error('Slot niet gevonden');
    if (lock.status === 'installed') throw new Error('Een geïnstalleerd slot kun je niet zomaar verplaatsen.');
    const onDoor = this.db.doors.find((d) => d.lock_id === lockId);
    if (onDoor && onDoor.organization_id !== orgId) throw new Error(`Dit slot hangt aan deur "${onDoor.name}"; ontkoppel het eerst.`);
    lock.organization_id = orgId;
    lock.status = orgId ? 'assigned' : 'in_stock';
    this.audit(orgId, 'lock.assign', 'locks', lockId);
    return this.wait(lock);
  }
  async emergencyResetLock(lockId: string) {
    this.assertWritable();
    const lock = this.db.locks.find((l) => l.id === lockId);
    if (!lock) throw new Error('Slot niet gevonden');
    if (!lock.provider_device_id) throw new Error('Dit slot is niet gekoppeld aan een apparaat.');
    lock.provider_device_id = null;
    lock.status = lock.organization_id ? 'assigned' : 'in_stock';
    this.audit(lock.organization_id, 'lock.factory_reset', 'locks', lockId);
    return this.wait(undefined);
  }
  async setLockModel(lockId: string, model: LockModel) {
    this.assertWritable();
    const lock = this.db.locks.find((l) => l.id === lockId);
    if (!lock) throw new Error('Slot niet gevonden');
    if (lock.provider_device_id) throw new Error('Dit slot is al geïnstalleerd en gekoppeld; het type ligt dan vast.');
    lock.model = model;
    this.audit(lock.organization_id, 'lock.model', 'locks', lockId);
    return this.wait(lock);
  }
  async markBatteryReplaced(lockId: string) {
    this.assertWritable();
    const lock = this.db.locks.find((l) => l.id === lockId);
    if (!lock) throw new Error('Slot niet gevonden');
    const now = this.nowIso();
    lock.battery_pct = 100;
    lock.battery_reported_at = now;
    lock.battery_alerted_at = null;
    lock.last_seen_at = now;
    this.audit(lock.organization_id, 'lock.battery_replaced', 'locks', lockId, { after: { battery_pct: 100 } });
    return this.wait(undefined);
  }
  private lockConfig = new Map<string, LockConfigState>();
  async setDoorAutoLock(doorId: string, enabled: boolean, seconds: number) {
    this.assertWritable();
    const door = this.db.doors.find((d) => d.id === doorId);
    if (!door) throw new Error('Deur niet gevonden');
    this.assertClubAdmin(door.organization_id);
    if (!Number.isInteger(seconds) || seconds < 3 || seconds > 1800) throw new Error('Kies een tijd tussen 3 seconden en 30 minuten.');
    door.auto_lock_enabled = enabled;
    door.auto_lock_seconds = seconds;
    if (door.lock_id) {
      this.enqueue(door.organization_id, door.lock_id, 'set_schedule');
      this.lockConfig.set(doorId, 'pending');
    }
    this.audit(door.organization_id, 'door.auto_lock', 'doors', doorId, { after: { enabled, seconds } });
    return this.wait(undefined);
  }
  async getDoorLockConfigState(doorId: string) {
    const door = this.db.doors.find((d) => d.id === doorId);
    if (!door?.lock_id) return this.wait('no_lock' as LockConfigState);
    return this.wait(this.lockConfig.get(doorId) ?? ('applied' as LockConfigState));
  }
  async setPassageMode(doorId: string, enabled: boolean) {
    this.assertWritable();
    const door = this.db.doors.find((d) => d.id === doorId);
    if (!door?.lock_id) throw new Error('Deur heeft geen slot');
    if (!door.passage_mode_allowed) throw new Error('Passagemodus is voor deze deur niet toegestaan.');
    this.enqueue(door.organization_id, door.lock_id, 'set_schedule');
    this.audit(door.organization_id, 'door.passage_mode', 'doors', doorId, { after: { enabled } });
    return this.wait(undefined);
  }

  // ---------- locaties en deuren (spiegelt save_location / save_door / link_lock_to_door / …) ----------
  /** Clubbeheerder, platformbeheerder of toegewezen installateur (zoals app.can_install_write). */
  private assertInstallWrite(orgId: string) {
    this.assertWritable();
    const r = this.actor.role;
    if (r === 'platform_admin' || r === null) return;
    if (r === 'installer') {
      const ok = this.db.installer_assignments.some(
        (a) => a.organization_id === orgId && a.profile_id === this.actor.profileId && new Date(a.valid_until) > this.now(),
      );
      if (ok) return;
    }
    if (this.db.memberships.some((m) => m.organization_id === orgId && m.profile_id === this.actor.profileId && m.role === 'club_admin')) return;
    throw new Error('Je hebt geen rechten om deuren te beheren.');
  }
  async saveLocation(orgId: string, input: LocationInput) {
    this.assertInstallWrite(orgId);
    const name = input.name.trim();
    if (name.length < 2) throw new Error('Vul de naam van de locatie in.');
    if (this.db.locations.some((l) => l.organization_id === orgId && l.name.toLowerCase() === name.toLowerCase() && l.id !== input.id)) {
      throw new Error('Er is al een locatie met deze naam.');
    }
    const raw = rawAddress(input);
    const firstErr = Object.values(validateAddress(raw))[0];
    if (firstErr) throw new Error(firstErr);
    const addr = cleanAddress(raw);
    const address = formatAddress({ address: null, ...addr }) || null;
    if (input.id) {
      const loc = this.db.locations.find((l) => l.id === input.id && l.organization_id === orgId);
      if (!loc) throw new Error('Locatie niet gevonden');
      const before = { name: loc.name, address: loc.address };
      Object.assign(loc, { name, address, ...addr });
      this.audit(orgId, 'location.update', 'locations', loc.id, { before, after: { name, address } });
      return this.wait(loc.id);
    }
    const id = uid('loc');
    this.db.locations.push({ id, organization_id: orgId, name, address, ...addr, provider: 'tuya', gateway_device_id: null });
    this.audit(orgId, 'location.create', 'locations', id, { after: { name, address } });
    return this.wait(id);
  }
  async deleteLocation(locationId: string) {
    const loc = this.db.locations.find((l) => l.id === locationId);
    if (!loc) throw new Error('Locatie niet gevonden');
    this.assertClubAdmin(loc.organization_id);
    if (this.db.doors.some((d) => d.location_id === locationId)) throw new Error('Er staan nog deuren op deze locatie.');
    this.db.locations = this.db.locations.filter((l) => l.id !== locationId);
    this.db.buildings = this.db.buildings.filter((b) => b.location_id !== locationId);
    this.audit(loc.organization_id, 'location.delete', 'locations', locationId, { before: { name: loc.name } });
    return this.wait(undefined);
  }

  // ---------- gebouwen (spiegelt save_building / archive_building) ----------
  async listBuildings(orgId: string) {
    return this.wait(this.db.buildings.filter((b) => b.organization_id === orgId).sort((a, b) => a.name.localeCompare(b.name)));
  }
  async saveBuilding(orgId: string, input: BuildingInput) {
    this.assertInstallWrite(orgId);
    const name = input.name.trim();
    const existing = input.id ? this.db.buildings.find((b) => b.id === input.id && b.organization_id === orgId) : undefined;
    if (input.id && !existing) throw new Error('Gebouw niet gevonden');
    if (existing?.archived_at) throw new Error('Dit gebouw is gearchiveerd.');
    if (!this.db.locations.some((l) => l.id === input.location_id && l.organization_id === orgId)) throw new Error('Kies een locatie van deze club.');
    if (name.length < 2) throw new Error('Vul de naam van het gebouw in.');
    if (
      this.db.buildings.some(
        (b) => b.location_id === input.location_id && !b.archived_at && b.name.toLowerCase() === name.toLowerCase() && b.id !== input.id,
      )
    ) {
      throw new Error('Er is op deze locatie al een gebouw met deze naam.');
    }
    if (existing && existing.location_id !== input.location_id && this.db.doors.some((d) => d.building_id === existing.id)) {
      throw new Error('Dit gebouw heeft deuren; het kan niet naar een andere locatie.');
    }
    const notes = input.notes?.trim() || null;
    if (existing) {
      const before = { name: existing.name, location_id: existing.location_id };
      Object.assign(existing, { name, notes, location_id: input.location_id });
      this.audit(orgId, 'building.update', 'buildings', existing.id, { before, after: { name, location_id: input.location_id } });
      return this.wait(existing.id);
    }
    const b: Building = { id: uid('bld'), organization_id: orgId, location_id: input.location_id, name, notes, created_at: this.nowIso(), archived_at: null };
    this.db.buildings.push(b);
    this.audit(orgId, 'building.create', 'buildings', b.id, { after: { name, location_id: input.location_id } });
    return this.wait(b.id);
  }
  async archiveBuilding(buildingId: string) {
    const b = this.db.buildings.find((x) => x.id === buildingId);
    if (!b) throw new Error('Gebouw niet gevonden');
    this.assertInstallWrite(b.organization_id);
    if (b.archived_at) return this.wait(undefined);
    if (this.db.doors.some((d) => d.building_id === b.id && !d.archived_at)) {
      throw new Error('Er zitten nog deuren in dit gebouw; verplaats of archiveer ze eerst.');
    }
    b.archived_at = this.nowIso();
    this.audit(b.organization_id, 'building.archive', 'buildings', b.id, { before: { name: b.name, location_id: b.location_id } });
    return this.wait(undefined);
  }
  async saveDoor(orgId: string, input: DoorInput) {
    this.assertInstallWrite(orgId);
    const isAdmin = this.actor.role !== 'installer';
    const name = input.name.trim();
    const door = input.id ? this.db.doors.find((d) => d.id === input.id && d.organization_id === orgId && !d.archived_at) : undefined;
    if (input.id && !door) throw new Error('Deur niet gevonden');
    const building = this.db.buildings.find((b) => b.id === input.building_id && b.organization_id === orgId);
    if (!building) throw new Error('Kies een gebouw van deze club.');
    if (building.archived_at && building.id !== door?.building_id) throw new Error('Dit gebouw is gearchiveerd.');
    if (name.length < 2) throw new Error('Vul de naam van de ruimte in.');
    if (
      this.db.doors.some(
        (d) => d.building_id === building.id && !d.archived_at && d.name.toLowerCase() === name.toLowerCase() && d.id !== input.id,
      )
    ) {
      throw new Error('Er is in dit gebouw al een ruimte met deze naam.');
    }
    if (
      !isAdmin &&
      ((input.escape_route != null && input.escape_route !== (door?.escape_route ?? false)) ||
        (input.passage_mode_allowed != null && input.passage_mode_allowed !== (door?.passage_mode_allowed ?? false)))
    ) {
      throw new Error('Alleen de clubbeheerder bepaalt nooduitgang en passagemodus.');
    }
    if (door) {
      const before = { name: door.name, building_id: door.building_id ?? null, location_id: door.location_id, kind: door.kind };
      Object.assign(door, {
        location_id: building.location_id,
        building_id: building.id,
        name,
        kind: input.kind,
        escape_route: input.escape_route ?? door.escape_route,
        passage_mode_allowed: input.passage_mode_allowed ?? door.passage_mode_allowed,
      });
      this.audit(orgId, 'door.update', 'doors', door.id, {
        before,
        after: { name, building_id: building.id, location_id: building.location_id, kind: input.kind },
      });
      return this.wait(door.id);
    }
    const created: Door = {
      id: uid('door'),
      organization_id: orgId,
      location_id: building.location_id,
      building_id: building.id,
      name,
      kind: input.kind,
      lock_id: null,
      expected_model: null,
      door_thickness_mm: null,
      escape_route: input.escape_route ?? false,
      passage_mode_allowed: input.passage_mode_allowed ?? false,
      created_at: this.nowIso(),
      archived_at: null,
    };
    this.db.doors.push(created);
    this.audit(orgId, 'door.create', 'doors', created.id, {
      after: { name, building_id: building.id, location_id: building.location_id, kind: input.kind },
    });
    return this.wait(created.id);
  }
  async archiveDoor(doorId: string) {
    const door = this.db.doors.find((d) => d.id === doorId);
    if (!door) throw new Error('Deur niet gevonden');
    this.assertClubAdmin(door.organization_id);
    if (door.lock_id) throw new Error('Er hangt nog een slot aan deze deur; ontkoppel het eerst.');
    door.archived_at = this.nowIso();
    this.audit(door.organization_id, 'door.archive', 'doors', doorId, { before: { name: door.name } });
    return this.wait(undefined);
  }
  async linkLockToDoor(doorId: string, lockId: string) {
    const door = this.db.doors.find((d) => d.id === doorId && !d.archived_at);
    if (!door) throw new Error('Deur niet gevonden');
    this.assertInstallWrite(door.organization_id);
    const lock = this.db.locks.find((l) => l.id === lockId);
    if (!lock) throw new Error('Slot niet gevonden');
    if (lock.organization_id !== door.organization_id) throw new Error('Dit slot is niet aan deze club toegewezen.');
    if (door.lock_id === lockId) return this.wait(undefined);
    if (door.lock_id) throw new Error('Deze deur heeft al een slot; ontkoppel dat eerst.');
    const other = this.db.doors.find((d) => d.lock_id === lockId);
    if (other) throw new Error(`Dit slot hangt al aan deur "${other.name}".`);
    if (lock.status !== 'assigned' && lock.status !== 'installed') throw new Error('Dit slot is defect of afgeschreven.');
    if (lock.status === 'installed' && this.actor.role !== 'platform_admin' && this.actor.role !== null) {
      throw new Error('Dit slot staat als geïnstalleerd geregistreerd; alleen Antreja kan het opnieuw koppelen.');
    }
    if (door.expected_model && door.expected_model !== lock.model) {
      throw new Error(`Deze deur verwacht een ${modelName(door.expected_model)}, dit slot is een ${modelName(lock.model)}.`);
    }
    door.lock_id = lockId;
    this.audit(door.organization_id, 'door.link_lock', 'doors', doorId, { after: { lock_id: lockId, serial: lock.serial, model: lock.model } });
    return this.wait(undefined);
  }
  async unlinkLockFromDoor(doorId: string) {
    const door = this.db.doors.find((d) => d.id === doorId);
    if (!door) throw new Error('Deur niet gevonden');
    this.assertInstallWrite(door.organization_id);
    if (!door.lock_id) return this.wait(undefined);
    const lock = this.db.locks.find((l) => l.id === door.lock_id)!;
    if (lock.status === 'installed' && this.actor.role !== 'platform_admin' && this.actor.role !== null) {
      throw new Error('Dit slot is al geïnstalleerd; alleen Antreja kan het ontkoppelen.');
    }
    door.lock_id = null;
    if (lock.status === 'installed') lock.status = 'assigned';
    this.recompile(door.organization_id, this.db.lock_grants.filter((g) => g.lock_id === lock.id).map((g) => g.person_id));
    this.audit(door.organization_id, 'door.unlink_lock', 'doors', doorId, { before: { lock_id: lock.id, serial: lock.serial, model: lock.model } });
    return this.wait(undefined);
  }
  async listLockPlacements(): Promise<LockPlacement[]> {
    return this.wait(
      this.db.doors
        .filter((d) => d.lock_id && !d.archived_at)
        .map((d) => ({
          lock_id: d.lock_id!,
          door_id: d.id,
          door_name: d.name,
          location_name: this.db.locations.find((l) => l.id === d.location_id)?.name ?? '',
          organization_id: d.organization_id,
        })),
    );
  }

  // ---------- persons & groups ----------
  async listPersons(orgId: string) {
    return this.wait(this.db.persons.filter((p) => p.organization_id === orgId && !p.archived_at));
  }
  async createPerson(orgId: string, input: PersonInput) {
    this.assertWritable();
    if (input.external_ref && this.db.persons.some((p) => p.organization_id === orgId && p.external_ref === input.external_ref)) {
      throw new Error('Er bestaat al een persoon met dit relatienummer.');
    }
    const person: Person = {
      id: uid('pers'),
      organization_id: orgId,
      profile_id: null,
      ...input,
      biometric_consent_at: null,
      created_at: this.nowIso(),
      archived_at: null,
    };
    this.db.persons.push(person);
    this.audit(orgId, 'person.create', 'persons', person.id);
    return this.wait(person);
  }
  async createPersonInGroup(groupId: string, input: PersonInput, validUntil: string | null) {
    this.assertWritable();
    const group = this.db.groups.find((g) => g.id === groupId && !g.archived_at);
    if (!group) throw new Error('Groep niet gevonden');
    if (this.actor.role === 'group_manager' && !this.db.group_managers.some((m) => m.group_id === group.id && m.profile_id === this.actor.profileId)) {
      throw new Error('Je beheert deze groep niet.');
    }
    const person = await this.createPerson(group.organization_id, input);
    await this.addGroupMember({ group_id: groupId, person_id: person.id, valid_until: validUntil });
    return person;
  }
  async archivePerson(personId: string) {
    this.assertWritable();
    const p = this.db.persons.find((x) => x.id === personId);
    if (!p) throw new Error('Persoon niet gevonden');
    p.archived_at = this.nowIso();
    this.recompile(p.organization_id, [p.id]);
    this.audit(p.organization_id, 'person.archive', 'persons', p.id);
    return this.wait(undefined);
  }
  // ---------- app-uitnodigingen (spiegelt create_person_invite / person_app_status) ----------
  private managesPerson(personId: string) {
    const groups = new Set(this.db.group_managers.filter((m) => m.profile_id === this.actor.profileId).map((m) => m.group_id));
    return this.db.group_members.some((m) => m.person_id === personId && groups.has(m.group_id));
  }
  private openInvite(personId: string) {
    return this.db.person_invites
      .filter((i) => i.person_id === personId && !i.accepted_at && !i.revoked_at)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  }
  async setMyTheme(profileId: string, theme: 'system' | 'light' | 'dark'): Promise<void> {
    const p = this.db.profiles.find((x) => x.id === profileId);
    if (p) p.ui_theme = theme;
    return this.wait(undefined);
  }
  // ---------- hulpteksten (spiegelt public.faq_items) ----------
  private faqRows: FaqRow[] | null = null;
  private faq(): FaqRow[] {
    if (!this.faqRows) this.faqRows = faqRowsFromBuiltIn();
    return this.faqRows;
  }
  async listFaqItems(): Promise<FaqRow[]> {
    const staff = this.actor.role === 'platform_admin' || this.actor.role === 'support' || this.actor.role === null;
    return this.wait(this.faq().filter((r) => r.published || staff).map((r) => ({ ...r })));
  }
  async saveFaqItem(row: FaqRow): Promise<void> {
    this.assertWritable();
    if (!['platform_admin', null].includes(this.actor.role)) throw new Error('Alleen een Antreja-beheerder wijzigt hulpteksten.');
    const rows = this.faq();
    const i = rows.findIndex((r) => r.id === row.id);
    const next = { ...row, updated_at: this.nowIso() };
    if (i >= 0) rows[i] = next;
    else rows.push(next);
    return this.wait(undefined);
  }
  async deleteFaqItem(id: string): Promise<void> {
    this.assertWritable();
    if (!['platform_admin', null].includes(this.actor.role)) throw new Error('Alleen een Antreja-beheerder wijzigt hulpteksten.');
    this.faqRows = this.faq().filter((r) => r.id !== id);
    return this.wait(undefined);
  }
  // ---------- uitleenpassen (spiegelt create_loan_card / lend_loan_card / return_loan_card / loan_cards) ----------
  private canLend(personId: string) {
    return ['club_admin', 'platform_admin', null].includes(this.actor.role) || this.managesPerson(personId);
  }
  private loanCardStatus(personId: string): LoanCardRow['card_status'] {
    const cards = this.db.credentials.filter((c) => c.person_id === personId && c.type === 'card');
    if (cards.some((c) => c.status === 'active')) return 'active';
    if (cards.some((c) => c.status === 'pending_enrollment')) return 'pending';
    if (cards.some((c) => c.status === 'revoke_pending' || c.status === 'revoked')) return 'blocked';
    return 'none';
  }
  async listLoanCards(orgId: string): Promise<LoanCardRow[]> {
    const now = this.now();
    const rows = this.db.persons
      .filter((p) => p.organization_id === orgId && p.is_loan_card && !p.archived_at)
      .filter((p) => this.actor.role !== 'group_manager' || this.managesPerson(p.id))
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map((p) => {
        const l = this.db.card_loans.find((x) => x.person_id === p.id && !x.returned_at);
        return {
          person_id: p.id,
          label: p.full_name,
          group_ids: this.db.group_members.filter((m) => m.person_id === p.id).map((m) => m.group_id),
          card_status: this.loanCardStatus(p.id),
          loan_id: l?.id ?? null,
          guest_name: l?.guest_name ?? null,
          guest_club: l?.guest_club ?? null,
          loaned_at: l?.loaned_at ?? null,
          due_at: l?.due_at ?? null,
          overdue: !!l && new Date(l.due_at) < now,
        };
      });
    return this.wait(rows);
  }
  async createLoanCard(groupId: string, label: string): Promise<string> {
    this.assertWritable();
    const g = this.db.groups.find((x) => x.id === groupId && !x.archived_at);
    if (!g) throw new Error('Groep niet gevonden');
    if (!['club_admin', 'platform_admin', null].includes(this.actor.role)) throw new Error('Alleen de clubbeheerder maakt uitleenpassen aan.');
    const name = label.trim();
    if (name.length < 2) throw new Error('Geef de pas een naam, bijv. Uitleenpas 1.');
    if (this.db.persons.some((p) => p.organization_id === g.organization_id && p.is_loan_card && !p.archived_at && p.full_name.toLowerCase() === name.toLowerCase())) {
      throw new Error('Er is al een uitleenpas met deze naam.');
    }
    const id = `pers-loan-${Date.now()}`;
    const created = this.nowIso();
    this.db.persons.push({ id, organization_id: g.organization_id, profile_id: null, full_name: name, email: null, phone: null, external_ref: null, is_minor: false, biometric_consent_at: null, created_at: created, archived_at: null, is_loan_card: true });
    this.db.group_members.push({ group_id: groupId, person_id: id, valid_from: created, valid_until: null, added_by: this.actor.profileId });
    this.recompile(g.organization_id, [id]);
    this.audit(g.organization_id, 'loan_card.create', 'persons', id);
    return this.wait(id);
  }
  async lendLoanCard(personId: string, guestName: string, guestClub: string | null, dueAt: string): Promise<void> {
    this.assertWritable();
    const p = this.db.persons.find((x) => x.id === personId && x.is_loan_card && !x.archived_at);
    if (!p) throw new Error('Uitleenpas niet gevonden');
    if (!this.canLend(p.id)) throw new Error('Alleen de groepsbeheerder van deze pas of de clubbeheerder mag uitlenen.');
    if (guestName.trim().length < 2) throw new Error('Vul de naam van de gast in.');
    const due = new Date(dueAt);
    if (!(due > this.now()) || due.getTime() > this.now().getTime() + 7 * 86400_000) throw new Error('Kies een inleverdatum binnen 7 dagen.');
    if (this.db.card_loans.some((l) => l.person_id === p.id && !l.returned_at)) throw new Error(`${p.full_name} is nog uitgeleend; neem hem eerst in.`);
    if (this.loanCardStatus(p.id) !== 'active') throw new Error(`${p.full_name} is nog niet ingeleerd of is geblokkeerd; vraag de installateur.`);
    const id = `loan-${Date.now()}`;
    this.db.card_loans.push({ id, organization_id: p.organization_id, person_id: p.id, guest_name: guestName.trim(), guest_club: guestClub?.trim() || null, loaned_at: this.nowIso(), due_at: due.toISOString(), returned_at: null, issued_by: this.actor.profileId, returned_by: null, created_at: this.nowIso() });
    this.audit(p.organization_id, 'loan_card.lend', 'card_loans', id);
    return this.wait(undefined);
  }
  async returnLoanCard(personId: string): Promise<void> {
    this.assertWritable();
    const p = this.db.persons.find((x) => x.id === personId && x.is_loan_card);
    if (!p) throw new Error('Uitleenpas niet gevonden');
    if (!this.canLend(p.id)) throw new Error('Alleen de groepsbeheerder van deze pas of de clubbeheerder mag innemen.');
    const l = this.db.card_loans.find((x) => x.person_id === p.id && !x.returned_at);
    if (!l) throw new Error(`${p.full_name} is niet uitgeleend.`);
    l.returned_at = this.nowIso();
    l.returned_by = this.actor.profileId;
    this.audit(p.organization_id, 'loan_card.return', 'card_loans', l.id);
    return this.wait(undefined);
  }
  async loanCardHistory(personId: string): Promise<CardLoan[]> {
    return this.wait(this.db.card_loans.filter((l) => l.person_id === personId).sort((a, b) => b.loaned_at.localeCompare(a.loaned_at)));
  }
  // ---------- clubinstallateur (spiegelt assign_club_installer / end_club_installer / club_installers) ----------
  async listClubInstallers(orgId: string): Promise<ClubInstallerRow[]> {
    const now = this.now();
    const rows = this.db.installer_assignments
      .filter((a) => a.organization_id === orgId)
      .map((a) => {
        const person = this.db.persons.find((p) => p.organization_id === orgId && p.profile_id === a.profile_id && !p.archived_at);
        const profile = this.db.profiles.find((p) => p.id === a.profile_id);
        return {
          profile_id: a.profile_id,
          person_id: person?.id ?? null,
          full_name: person?.full_name ?? profile?.full_name ?? 'Onbekend',
          kind: a.kind ?? 'antreja',
          valid_until: a.valid_until,
          active: new Date(a.valid_until) > now,
        } as ClubInstallerRow;
      });
    return this.wait(rows);
  }
  async assignClubInstaller(personId: string, validUntil: string): Promise<void> {
    this.assertWritable();
    const p = this.db.persons.find((x) => x.id === personId && !x.archived_at);
    if (!p) throw new Error('Persoon niet gevonden');
    if (!['club_admin', 'platform_admin', null].includes(this.actor.role)) throw new Error('Alleen de clubbeheerder kan een installateur aanwijzen.');
    if (!p.profile_id) throw new Error('Nodig deze persoon eerst uit voor de app.');
    const until = new Date(validUntil);
    if (!(until > this.now()) || until.getTime() > this.now().getTime() + 366 * 86400_000) throw new Error('Kies een einddatum binnen een jaar.');
    const existing = this.db.installer_assignments.find((a) => a.profile_id === p.profile_id && a.organization_id === p.organization_id);
    if (existing && (existing.kind ?? 'antreja') === 'antreja') throw new Error('Deze persoon is al installateur via Antreja.');
    this.db.installer_assignments = this.db.installer_assignments.filter((a) => a !== existing);
    this.db.installer_assignments.push({ organization_id: p.organization_id, profile_id: p.profile_id, valid_until: until.toISOString(), kind: 'club', assigned_by: this.actor.profileId });
    this.audit(p.organization_id, 'installer.club_assign', 'installer_assignments', p.profile_id);
    return this.wait(undefined);
  }
  async endClubInstaller(personId: string): Promise<void> {
    this.assertWritable();
    const p = this.db.persons.find((x) => x.id === personId);
    if (!p?.profile_id) throw new Error('Persoon niet gevonden');
    const a = this.db.installer_assignments.find((x) => x.profile_id === p.profile_id && x.organization_id === p.organization_id && x.kind === 'club');
    if (!a) throw new Error('Deze persoon is geen clubinstallateur.');
    this.db.installer_assignments = this.db.installer_assignments.filter((x) => x !== a);
    this.audit(p.organization_id, 'installer.club_end', 'installer_assignments', p.profile_id);
    return this.wait(undefined);
  }
  async listPersonAppStatus(orgId: string): Promise<PersonAppStatusRow[]> {
    const rows = this.db.persons
      .filter((p) => p.organization_id === orgId && !p.archived_at)
      .filter((p) => this.actor.role !== 'group_manager' || this.managesPerson(p.id))
      .map((p) => {
        const inv = this.openInvite(p.id);
        return {
          person_id: p.id,
          profile_id: p.profile_id,
          last_sign_in_at: p.profile_id ? this.db.last_sign_in[p.profile_id] ?? null : null,
          invite_id: inv?.id ?? null,
          invite_email: inv?.email ?? null,
          invited_at: inv?.created_at ?? null,
          invite_expires_at: inv?.expires_at ?? null,
          guardian_informed_at: inv?.guardian_informed_at ?? null,
        };
      });
    return this.wait(rows);
  }
  async invitePerson(input: InvitePersonInput): Promise<InvitePersonResult> {
    this.assertWritable();
    const p = this.db.persons.find((x) => x.id === input.person_id && !x.archived_at);
    if (!p) throw new Error('Persoon niet gevonden');
    const isAdmin = this.actor.role === 'club_admin' || this.actor.role === 'platform_admin' || this.actor.role === null;
    if (!isAdmin && !(this.actor.role === 'group_manager' && this.managesPerson(p.id))) {
      throw new Error('Je mag alleen leden van je eigen groep uitnodigen.');
    }
    if (p.profile_id) throw new Error('Deze persoon heeft de app al.');
    const email = (input.email ?? p.email ?? '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Dit e-mailadres klopt niet.');
    if (p.is_minor && !input.guardian_informed) throw new Error('Bevestig eerst dat de ouder of verzorger is geïnformeerd.');
    const hourAgo = this.now().getTime() - 3600_000;
    if (this.db.person_invites.filter((i) => i.organization_id === p.organization_id && new Date(i.created_at).getTime() > hourAgo).length >= 50) {
      throw new Error('Er zijn het afgelopen uur al 50 uitnodigingen verstuurd. Probeer het over een uur opnieuw.');
    }
    const owner = this.db.persons.find((x) => x.profile_id && x.email?.toLowerCase() === email);
    if (owner && owner.organization_id === p.organization_id && owner.id !== p.id) {
      throw new Error('Dit e-mailadres hoort al bij een andere persoon in deze club.');
    }
    p.email = email;
    for (const i of this.db.person_invites) if (i.person_id === p.id && !i.accepted_at && !i.revoked_at) i.revoked_at = this.nowIso();
    const inv = {
      id: uid('inv'),
      organization_id: p.organization_id,
      person_id: p.id,
      email,
      created_by: this.actor.profileId,
      created_at: this.nowIso(),
      expires_at: new Date(this.now().getTime() + 30 * 86400_000).toISOString(),
      accepted_at: null,
      revoked_at: null,
      guardian_informed_at: p.is_minor ? this.nowIso() : null,
    };
    this.db.person_invites.push(inv);
    this.audit(p.organization_id, 'person_invite.create', 'persons', p.id);
    return this.wait({ invite_id: inv.id, email, status: owner ? 'existing_user' : 'invited' });
  }
  private assertAppAdmin() {
    this.assertWritable();
    if (this.actor.role === 'group_manager') throw new Error('Alleen een clubbeheerder kan dit.');
  }
  async revokePersonInvite(inviteId: string) {
    this.assertAppAdmin();
    const inv = this.db.person_invites.find((i) => i.id === inviteId);
    if (!inv) throw new Error('Uitnodiging niet gevonden');
    if (inv.accepted_at) throw new Error('Uitnodiging is al gebruikt; ontkoppel de app.');
    inv.revoked_at ??= this.nowIso();
    this.audit(inv.organization_id, 'person_invite.revoke', 'persons', inv.person_id);
    return this.wait(undefined);
  }
  async unlinkPersonApp(personId: string) {
    this.assertAppAdmin();
    const p = this.db.persons.find((x) => x.id === personId);
    if (!p) throw new Error('Persoon niet gevonden');
    for (const i of this.db.person_invites) if (i.person_id === p.id && !i.accepted_at && !i.revoked_at) i.revoked_at = this.nowIso();
    if (p.profile_id) {
      const prof = p.profile_id;
      p.profile_id = null;
      this.db.memberships = this.db.memberships.filter(
        (m) => !(m.organization_id === p.organization_id && m.profile_id === prof && m.role === 'member'),
      );
      this.audit(p.organization_id, 'person.app_unlink', 'persons', p.id);
    }
    return this.wait(undefined);
  }

  async listGroups(orgId: string) {
    return this.wait(this.db.groups.filter((g) => g.organization_id === orgId && !g.archived_at));
  }
  async createGroup(orgId: string, input: GroupInput) {
    this.assertWritable();
    const g: Group = {
      id: uid('grp'),
      organization_id: orgId,
      name: input.name,
      season_label: input.season_label,
      valid_from: this.nowIso(),
      valid_until: input.valid_until,
      created_at: this.nowIso(),
      archived_at: null,
    };
    this.db.groups.push(g);
    this.audit(orgId, 'group.create', 'groups', g.id);
    return this.wait(g);
  }
  async duplicateGroup(groupId: string, input: GroupInput & { copy_member_ids: string[]; member_valid_until: string | null }) {
    this.assertWritable();
    const src = this.db.groups.find((g) => g.id === groupId);
    if (!src) throw new Error('Groep niet gevonden');
    const g = await this.createGroup(src.organization_id, input);
    for (const r of this.db.access_rules.filter((x) => x.group_id === groupId)) {
      this.db.access_rules.push({ ...r, id: uid('rule'), group_id: g.id });
    }
    for (const personId of input.copy_member_ids) {
      this.db.group_members.push({
        group_id: g.id,
        person_id: personId,
        valid_from: this.nowIso(),
        valid_until: input.member_valid_until,
        added_by: this.actor.profileId,
      });
    }
    this.recompile(src.organization_id, input.copy_member_ids);
    this.audit(src.organization_id, 'group.duplicate', 'groups', g.id);
    return this.wait(g);
  }
  async listGroupMembers(orgId: string) {
    const groupIds = new Set(this.db.groups.filter((g) => g.organization_id === orgId).map((g) => g.id));
    return this.wait(this.db.group_members.filter((m) => groupIds.has(m.group_id)));
  }
  async addGroupMember(input: { group_id: string; person_id: string; valid_until: string | null }) {
    this.assertWritable();
    const group = this.db.groups.find((g) => g.id === input.group_id);
    if (!group) throw new Error('Groep niet gevonden');
    if (this.actor.role === 'group_manager' && !this.db.group_managers.some((m) => m.group_id === group.id && m.profile_id === this.actor.profileId)) {
      throw new Error('Je beheert deze groep niet.');
    }
    if (group.valid_until && input.valid_until && input.valid_until > group.valid_until) {
      throw new Error('Einddatum ligt na de einddatum van de groep.');
    }
    if (this.db.group_members.some((m) => m.group_id === input.group_id && m.person_id === input.person_id)) {
      throw new Error('Deze persoon zit al in de groep.');
    }
    const m: GroupMember = {
      group_id: input.group_id,
      person_id: input.person_id,
      valid_from: this.nowIso(),
      valid_until: input.valid_until,
      added_by: this.actor.profileId,
    };
    this.db.group_members.push(m);
    this.recompile(group.organization_id, [input.person_id]);
    this.audit(group.organization_id, 'group_member.add', 'group_members', `${group.id}:${input.person_id}`);
    return this.wait(m);
  }
  async removeGroupMember(groupId: string, personId: string) {
    this.assertWritable();
    const group = this.db.groups.find((g) => g.id === groupId);
    if (!group) throw new Error('Groep niet gevonden');
    this.db.group_members = this.db.group_members.filter((m) => !(m.group_id === groupId && m.person_id === personId));
    this.recompile(group.organization_id, [personId]);
    this.audit(group.organization_id, 'group_member.remove', 'group_members', `${groupId}:${personId}`);
    return this.wait(undefined);
  }
  async listGroupManagers(orgId: string) {
    const groupIds = new Set(this.db.groups.filter((g) => g.organization_id === orgId).map((g) => g.id));
    return this.wait(
      this.db.group_managers
        .filter((m) => groupIds.has(m.group_id))
        .map((m) => ({ ...m, profile: this.db.profiles.find((p) => p.id === m.profile_id)! })),
    );
  }
  async listAccessRules(orgId: string) {
    return this.wait(this.db.access_rules.filter((r) => r.organization_id === orgId));
  }
  async saveAccessRules(groupId: string, rules: AccessRuleInput[]) {
    this.assertWritable();
    const group = this.db.groups.find((g) => g.id === groupId);
    if (!group) throw new Error('Groep niet gevonden');
    this.db.access_rules = this.db.access_rules.filter((r) => r.group_id !== groupId);
    const created: AccessRule[] = rules
      .filter((r) => r.weekdays > 0)
      .map((r) => ({ ...r, id: uid('rule'), organization_id: group.organization_id, group_id: groupId }));
    this.db.access_rules.push(...created);
    const members = this.db.group_members.filter((m) => m.group_id === groupId).map((m) => m.person_id);
    this.recompile(group.organization_id, members);
    this.audit(group.organization_id, 'access_rules.save', 'groups', groupId);
    return this.wait(created);
  }

  // ---------- credentials ----------
  async listCredentials(orgId: string) {
    return this.wait(this.db.credentials.filter((c) => c.organization_id === orgId));
  }
  async revokeCredential(credentialId: string) {
    this.assertWritable();
    const c = this.db.credentials.find((x) => x.id === credentialId);
    if (!c) throw new Error('Credential niet gevonden');
    c.status = 'revoke_pending';
    if (c.lock_id) this.enqueue(c.organization_id, c.lock_id, 'remove_credential');
    else {
      // app credential: remove member from every lock the person has
      for (const g of this.db.lock_grants.filter((x) => x.person_id === c.person_id)) {
        this.enqueue(c.organization_id, g.lock_id, 'remove_credential');
      }
    }
    this.audit(c.organization_id, 'credential.revoke', 'credentials', c.id);
    return this.wait(c);
  }
  async listLockGrants(orgId: string) {
    return this.wait(this.db.lock_grants.filter((g) => g.organization_id === orgId));
  }
  async listLockHardening(orgId: string) {
    return this.wait(this.db.lock_hardening.filter((h) => h.organization_id === orgId));
  }
  async listSyncJobs(orgId: string | null) {
    return this.wait(orgId ? this.db.sync_jobs.filter((j) => j.organization_id === orgId) : this.db.sync_jobs);
  }

  // ---------- codes ----------
  async listAccessCodes(orgId: string) {
    return this.wait(this.db.access_codes.filter((c) => c.organization_id === orgId));
  }
  async createAccessCode(orgId: string, input: AccessCodeInput): Promise<CreatedCode> {
    this.assertWritable();
    const lock = this.db.locks.find((l) => l.id === input.lock_id);
    if (lock?.model !== 'D1_K') throw new Error(`Tijdelijke codes kunnen alleen op een ${MODEL_LABEL.D1_K} met codeklavier.`);
    const code: AccessCode = {
      id: uid('code'),
      organization_id: orgId,
      ...input,
      revoked_at: null,
      created_by: this.actor.profileId ?? 'demo',
      created_at: this.nowIso(),
    };
    this.db.access_codes.unshift(code);
    // Offline codes are computed by the provider, no BLE job needed (spec 03).
    if (input.kind === 'online_custom') this.enqueue(orgId, input.lock_id, 'create_code');
    this.audit(orgId, 'access_code.create', 'access_codes', code.id);
    const secret = String(Math.floor(10_000_000 + Math.random() * 89_999_999));
    return this.wait({ code, secret });
  }
  async revokeAccessCode(codeId: string) {
    this.assertWritable();
    const c = this.db.access_codes.find((x) => x.id === codeId);
    if (!c) throw new Error('Code niet gevonden');
    c.revoked_at = this.nowIso();
    this.enqueue(c.organization_id, c.lock_id, 'revoke_code');
    this.audit(c.organization_id, 'access_code.revoke', 'access_codes', c.id);
    return this.wait(undefined);
  }

  // ---------- logs ----------
  async listAccessEvents(orgId: string, filter: EventFilter = {}) {
    return this.wait(
      this.db.access_events.filter(
        (e) =>
          e.organization_id === orgId &&
          (!filter.door_id || e.door_id === filter.door_id) &&
          (!filter.person_id || e.person_id === filter.person_id) &&
          (!filter.result || e.result === filter.result) &&
          (!filter.since || e.occurred_at >= filter.since),
      ),
    );
  }
  async listAuditLog(orgId: string | null) {
    return this.wait(orgId ? this.db.audit_log.filter((a) => a.organization_id === orgId) : this.db.audit_log);
  }

  /** Spiegelt public.club_audit_log: alleen club_admin van de club en Antreja (platform_admin/support). */
  async listClubAudit(orgId: string, filter: ClubAuditFilter): Promise<ClubAuditPage> {
    const r = this.actor.role;
    const isAdmin = this.db.memberships.some((m) => m.organization_id === orgId && m.profile_id === this.actor.profileId && m.role === 'club_admin');
    if (!(r === null || r === 'platform_admin' || r === 'support' || isAdmin)) throw new Error('Geen rechten om het beheerlogboek van deze club te bekijken.');
    const q = filter.search?.trim().toLowerCase() || null;
    const rows = this.db.audit_log
      .filter((a) => a.organization_id === orgId)
      .filter((a) => (!filter.from || a.created_at >= filter.from) && (!filter.to || a.created_at < filter.to))
      .map((a) => this.toClubAuditRow(a))
      .filter((a) => !filter.category || a.category === filter.category)
      .filter((a) => {
        if (!q) return true;
        const hay = [a.actor_name, a.action, ...Object.values(a.context), a.after?.name, a.before?.name, a.after?.label]
          .filter((x) => typeof x === 'string')
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((x, y) => y.created_at.localeCompare(x.created_at) || y.id - x.id);
    return this.wait({ rows: rows.slice(filter.offset, filter.offset + filter.limit), total: rows.length });
  }

  /** Namen bij een auditregel, zoals club_audit_log ze in de database opzoekt. */
  private toClubAuditRow(a: AuditEntry): ClubAuditRow {
    const db = this.db;
    const ctx: AuditContext = {};
    const after = a.after ?? null;
    const before = a.before ?? null;
    const ref = (k: string) => (after?.[k] ?? before?.[k]) as string | undefined;
    const parts = (a.entity_id ?? '').split(':');
    let doorId = a.entity === 'doors' ? a.entity_id ?? undefined : ref('door_id');
    let lockId = a.entity === 'locks' ? a.entity_id ?? undefined : ref('lock_id');
    let personId = a.entity === 'persons' ? a.entity_id ?? undefined : ref('person_id');
    let groupId = a.entity === 'groups' ? a.entity_id ?? undefined : ref('group_id');
    let buildingId = a.entity === 'buildings' ? a.entity_id ?? undefined : ref('building_id');
    let locationId = a.entity === 'locations' ? a.entity_id ?? undefined : ref('location_id');
    let subjectId: string | undefined;
    if (a.entity === 'group_members') {
      groupId = parts[0];
      personId = parts[1];
    }
    if (a.entity === 'memberships') subjectId = parts[1];
    if (a.entity === 'installer_assignments' || a.entity === 'profiles') subjectId = a.entity_id ?? undefined;
    if (a.entity === 'credentials') {
      const c = db.credentials.find((x) => x.id === a.entity_id);
      personId = personId ?? c?.person_id ?? undefined;
      lockId = lockId ?? c?.lock_id ?? undefined;
    }
    if (a.entity === 'access_codes') lockId = lockId ?? db.access_codes.find((x) => x.id === a.entity_id)?.lock_id;
    const lock = lockId ? db.locks.find((l) => l.id === lockId) : undefined;
    const door = db.doors.find((d) => d.id === doorId) ?? (lockId ? db.doors.find((d) => d.lock_id === lockId) : undefined);
    doorId = door?.id;
    buildingId = buildingId ?? door?.building_id ?? undefined;
    const building = db.buildings.find((b) => b.id === buildingId);
    locationId = locationId ?? building?.location_id ?? door?.location_id;
    const name = (after?.name ?? before?.name) as string | undefined;
    ctx.door = door?.name ?? (a.entity === 'doors' ? name : undefined);
    ctx.building = building?.name ?? (a.entity === 'buildings' ? name : undefined);
    ctx.location = db.locations.find((l) => l.id === locationId)?.name ?? (a.entity === 'locations' ? name : undefined);
    ctx.person = db.persons.find((p) => p.id === personId)?.full_name;
    ctx.group = db.groups.find((g) => g.id === groupId)?.name;
    ctx.subject = subjectId ? db.profiles.find((p) => p.id === subjectId)?.full_name : undefined;
    ctx.lock_serial = lock?.serial ?? ref('serial');
    ctx.lock_model = lock?.model ?? ref('model');
    for (const k of Object.keys(ctx) as (keyof AuditContext)[]) if (!ctx[k]) delete ctx[k];
    return {
      id: a.id,
      created_at: a.created_at,
      action: a.action,
      entity: a.entity,
      entity_id: a.entity_id,
      actor_profile_id: a.actor_profile_id,
      actor_name: db.profiles.find((p) => p.id === a.actor_profile_id)?.full_name ?? null,
      actor_role: a.actor_role,
      category: auditCategory(a.action, a.entity),
      before,
      after,
      context: ctx,
    };
  }


  // ---------- import ----------
  async importPersons(orgId: string, input: ImportInput): Promise<ImportResult> {
    this.assertWritable();
    let created = 0;
    let updated = 0;
    let memberships = 0;
    const touched: string[] = [];
    for (const row of input.rows) {
      let p = this.db.persons.find((x) => x.organization_id === orgId && row.external_ref && x.external_ref === row.external_ref);
      if (p) {
        Object.assign(p, { full_name: row.full_name, email: row.email, phone: row.phone, is_minor: row.is_minor, archived_at: null });
        updated++;
      } else {
        p = {
          id: uid('pers'),
          organization_id: orgId,
          profile_id: null,
          full_name: row.full_name,
          email: row.email,
          phone: row.phone,
          external_ref: row.external_ref,
          is_minor: row.is_minor,
          biometric_consent_at: null,
          created_at: this.nowIso(),
          archived_at: null,
        };
        this.db.persons.push(p);
        created++;
      }
      touched.push(p.id);
      for (const gid of input.group_ids) {
        if (this.db.group_members.some((m) => m.group_id === gid && m.person_id === p!.id)) continue;
        this.db.group_members.push({ group_id: gid, person_id: p.id, valid_from: this.nowIso(), valid_until: input.valid_until, added_by: this.actor.profileId });
        memberships++;
      }
    }
    this.recompile(orgId, touched);
    this.audit(orgId, 'persons.import', 'persons', `${created}+${updated}`);
    return this.wait({ created, updated, memberships });
  }

  // ---------- platform ----------
  private assertPlatformAdmin() {
    if (this.actor.role !== 'platform_admin' && this.actor.role !== null) throw new Error('Alleen een platformbeheerder kan het team beheren.');
  }
  private assertKeepsPlatformAdmin(profileId: string) {
    if (!this.db.platform_staff.some((s) => s.role === 'platform_admin' && s.profile_id !== profileId)) {
      throw new Error('Het Antreja-team moet minstens één platformbeheerder houden.');
    }
  }
  async listPlatformStaff(): Promise<StaffRow[]> {
    const rank: Record<PlatformRole, number> = { platform_admin: 0, installer: 1, support: 2 };
    const rows = this.db.platform_staff
      .map((s) => ({
        profile_id: s.profile_id,
        full_name: this.db.profiles.find((p) => p.id === s.profile_id)?.full_name ?? '',
        email: this.db.emails[s.profile_id] ?? null,
        role: s.role,
        created_at: this.db.organizations[0]?.created_at ?? this.nowIso(),
        last_sign_in_at: this.db.last_sign_in[s.profile_id] ?? null,
        totp_enabled: this.db.totp_profiles.includes(s.profile_id),
        active_assignments: this.db.installer_assignments.filter(
          (a) => a.profile_id === s.profile_id && new Date(a.valid_until) > this.now(),
        ).length,
      }))
      .sort((a, b) => rank[a.role] - rank[b.role] || a.full_name.localeCompare(b.full_name));
    return this.wait(rows);
  }
  async inviteStaff(input: StaffInviteInput): Promise<StaffInviteResult> {
    this.assertPlatformAdmin();
    const email = input.email.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) throw new Error('Dit e-mailadres klopt niet.');
    if (input.full_name.trim().length < 2) throw new Error('Vul de naam in.');
    const existing = Object.values(this.db.emails).some((e) => e.toLowerCase() === email);
    const profileId = this.findOrCreateProfile(email, input.full_name);
    if (this.db.platform_staff.some((s) => s.profile_id === profileId)) throw new Error('Deze persoon zit al in het Antreja-team.');
    this.db.platform_staff.push({ profile_id: profileId, role: input.role });
    this.audit(null, 'staff.add', 'platform_staff', profileId);
    return this.wait({ profile_id: profileId, role: input.role, status: existing ? 'existing_user' : 'invited' });
  }
  async setStaffRole(profileId: string, role: PlatformRole) {
    this.assertPlatformAdmin();
    if (profileId === this.actor.profileId) throw new Error('Je kunt je eigen rol niet wijzigen; vraag een collega.');
    const s = this.db.platform_staff.find((x) => x.profile_id === profileId);
    if (!s) throw new Error('Teamlid niet gevonden');
    if (s.role === role) return this.wait(undefined);
    if (s.role === 'platform_admin') this.assertKeepsPlatformAdmin(profileId);
    s.role = role;
    if (role === 'support') this.db.installer_assignments = this.db.installer_assignments.filter((a) => a.profile_id !== profileId);
    this.audit(null, 'staff.role', 'platform_staff', profileId);
    return this.wait(undefined);
  }
  async removeStaff(profileId: string) {
    this.assertPlatformAdmin();
    if (profileId === this.actor.profileId) throw new Error('Je kunt jezelf niet uit het team halen.');
    const s = this.db.platform_staff.find((x) => x.profile_id === profileId);
    if (!s) throw new Error('Teamlid niet gevonden');
    if (s.role === 'platform_admin') this.assertKeepsPlatformAdmin(profileId);
    this.db.platform_staff = this.db.platform_staff.filter((x) => x !== s);
    this.db.installer_assignments = this.db.installer_assignments.filter((a) => a.profile_id !== profileId);
    this.audit(null, 'staff.remove', 'platform_staff', profileId);
    return this.wait(undefined);
  }

  async listInstallers(): Promise<InstallerRow[]> {
    const rows = this.db.platform_staff
      .filter((s) => s.role === 'installer' || s.role === 'platform_admin')
      .map((s) => ({
        profile: this.db.profiles.find((p) => p.id === s.profile_id)!,
        assignments: this.db.installer_assignments.filter((a) => a.profile_id === s.profile_id),
      }));
    return this.wait(rows);
  }
  async assignInstaller(profileId: string, orgId: string, validUntil: string) {
    this.assertWritable();
    this.db.installer_assignments = this.db.installer_assignments.filter((a) => !(a.profile_id === profileId && a.organization_id === orgId));
    this.db.installer_assignments.push({ profile_id: profileId, organization_id: orgId, valid_until: validUntil });
    this.audit(orgId, 'installer.assign', 'installer_assignments', profileId);
    return this.wait(undefined);
  }

  async enableImpersonationWrite(orgId: string, reason: string) {
    if (reason.trim().length < 5) throw new Error('Geef een reden op (minimaal 5 tekens).');
    if (this.actor.role !== 'platform_admin' && this.actor.role !== null) throw new Error('Alleen een Antreja-beheerder kan wijzigen inschakelen.');
    this.audit(orgId, 'impersonate.write_enabled', 'organizations', orgId, { after: { reason } });
    return this.wait(undefined);
  }
  async startImpersonation(orgId: string, reason: string) {
    if (reason.trim().length < 5) throw new Error('Geef een reden op (minimaal 5 tekens).');
    this.audit(orgId, 'impersonate.start', 'organizations', orgId);
    return this.wait(undefined);
  }

  // ---------- demo ----------
  async simulateBleSync(lockId: string) {
    const now = this.nowIso();
    const open = this.db.sync_jobs.filter((j) => j.lock_id === lockId && ['queued', 'awaiting_ble', 'in_progress', 'failed'].includes(j.status));
    for (const j of open) {
      j.status = 'done';
      j.completed_at = now;
      j.attempts += 1;
    }
    for (const g of this.db.lock_grants.filter((x) => x.lock_id === lockId)) {
      if (g.sync_state === 'pending') {
        g.sync_state = 'synced';
        g.last_synced_at = now;
      } else if (g.sync_state === 'revoke_pending') g.sync_state = 'revoked';
    }
    for (const c of this.db.credentials.filter((x) => x.status === 'revoke_pending')) {
      const stillOpen = this.db.sync_jobs.some((j) => j.status === 'awaiting_ble' && j.action === 'remove_credential' && (j.lock_id === c.lock_id || !c.lock_id));
      if (c.lock_id === lockId || (!c.lock_id && !stillOpen)) c.status = 'revoked';
    }
    const lock = this.db.locks.find((l) => l.id === lockId);
    if (lock) {
      lock.last_sync_at = now;
      lock.last_seen_at = now;
      const door = this.db.doors.find((d) => d.lock_id === lockId);
      this.db.access_events.unshift({
        id: ++this.eventSeq,
        organization_id: lock.organization_id ?? '',
        lock_id: lockId,
        door_id: door?.id ?? null,
        person_id: null,
        occurred_at: now,
        received_at: now,
        method: 'app',
        result: 'granted',
      });
    }
    return this.wait(open.length);
  }
}
